import { useEffect, useMemo, useRef, useState } from 'react'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import AlternateEmailRoundedIcon from '@mui/icons-material/AlternateEmailRounded'
import AssignmentIndRoundedIcon from '@mui/icons-material/AssignmentIndRounded'
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import UploadRoundedIcon from '@mui/icons-material/UploadRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { firestoreDb } from '../services/firebase'
import { FormExperienceScreen } from './Forms/FormExperienceScreen'
import { useAuth } from '../context/useAuth'
import { useDisplayName } from '../hooks/useDisplayName'
import { usePersistedState } from '../hooks/usePersistedState'
import { useNotifications } from '../context/useNotifications'
import { useRaid } from '../context/useRaid'
import { useTimeline } from '../context/useTimeline'
import { useWorkspace } from '../context/useWorkspace'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { AdaptiveMultiSelectFilter, AppDialog, Button, Card, DatePicker, EmptyHubState, HiddenFileInput, IconButton, MiniLoader, RailPageShell, TextInput, TopHeaderSelect } from './Common'
import { SettingsRadioList } from './Common/SettingsRadioList'
import { useNls } from '../nls/nlsProvider'
import { Pressable } from './Common/Pressable'
import {
  uploadChecklistProof,
} from '../services/checklistAttachmentService'
import {
  buildChecklistProofAttachmentUpdates,
} from '../services/checklistProofService'
import {
  updateChecklistFields,
  updateChecklistWorkflowStatus,
} from '../services/checklistMutationService'
import { readCurrentCoordinates } from '../utils/geolocation'
import { subscribeAssignedFormRequests } from '../services/formRequestService'
import { useFormDefinition } from '../hooks/useFormDefinitions'
import { queryWorkspaceActivityPage } from '../services/activityService'
import type { FormRequest } from '../types/forms'
import type {
  ChecklistItem,
  ChecklistWorkflowStatus,
  NotificationRecord,
} from '../types/schema'
import {
  getChecklistDependencyBlockState,
  normalizeChecklistBlockedTodoPolicy,
} from '../utils/checklistBlockedTodoPolicy'
import {
  subscribeChecklistComments,
  subscribeChecklistProofs,
  type HelperChecklistComment,
  type HelperChecklistProof,
} from '../services/helper/helperChecklistDetailsService'
import { resolveAllowedTransitions } from '../services/helper/helperTaskService'
import { subscribeChecklistItemProjection, subscribeChecklistItemsView } from '../services/checklistReader'
import { isChecklistGate } from '../utils/checklistDependencyState'
import { currentOccurrenceKeyForItem } from '../services/recurringChecklistService'
import {
  normalizeMemberForYouStatus,
  resolveMemberForYouActionPolicy,
  resolveMemberForYouBoard,
} from './Member/memberForYouModel'
import { isModuleEnabled } from '../utils/workspaceModules'
import { useWorkspaceDay } from '../hooks/useWorkspaceDay'

type VisibilityScope = 'MINE' | 'ASSIGNED'
type QuickFilterKey =
  | 'OVERDUE'
  | 'DUE_SOON'
  | 'BLOCKED'
  | 'NEEDS_PROOF'
  | 'WITH_ATTACHMENTS'
  | 'RECENT'
  | 'COMPLETED'
  | 'MENTIONS'
  | 'PREVIOUSLY_MINE'
type SortMode =
  | 'DUE_ASC'
  | 'DUE_DESC'
  | 'UPDATED_DESC'
  | 'URGENCY_DESC'
  | 'IMPORTANCE_DESC'
  | 'STATUS'
  | 'ASSIGNEE'
  | 'TITLE'
type DueWindow = 'ANY' | 'OVERDUE' | 'TODAY' | 'THIS_WEEK' | 'FUTURE' | 'NO_DATE'
type UpdatedWindow = 'ANY' | '24H' | '7D' | '30D'
type DependencyFilter = 'ANY' | 'BLOCKED' | 'CLEAR'
type ProofFilter = 'ANY' | 'REQUIRED' | 'MISSING_REQUIRED' | 'UPLOADED'
type AttachmentFilter = 'ANY' | 'HAS' | 'NONE'
type FormFilter = 'ANY' | 'HAS' | 'NONE'
type BoardFilter = 'DASHBOARD' | 'ARCHIVE' | 'BIN'
type MemberOption = { userId: string; displayName: string }
type AttachmentEntry = {
  id: string
  kind: 'PROOF' | 'MEMO' | 'COMMENT'
  name: string
  url: string
  meta: string
}

type AdvancedFilters = {
  assigneeIds: string[]
  creatorIds: string[]
  statuses: ChecklistWorkflowStatus[]
  boards: BoardFilter[]
  dueWindow: DueWindow
  dependency: DependencyFilter
  proof: ProofFilter
  attachment: AttachmentFilter
  forms: FormFilter
  updatedWindow: UpdatedWindow
}

type ActivityEntry = {
  id: string
  summary: string
  actorName: string
  createdAt: Date | null
}

const DEFAULT_ADVANCED_FILTERS: AdvancedFilters = {
  assigneeIds: [],
  creatorIds: [],
  statuses: [],
  boards: [],
  dueWindow: 'ANY',
  dependency: 'ANY',
  proof: 'ANY',
  attachment: 'ANY',
  forms: 'ANY',
  updatedWindow: 'ANY',
}

const QUICK_FILTERS: Array<{ key: QuickFilterKey; label: string }> = [
  { key: 'OVERDUE', label: 'Overdue' },
  { key: 'DUE_SOON', label: 'Due soon' },
  { key: 'BLOCKED', label: 'Blocked' },
  { key: 'NEEDS_PROOF', label: 'Needs proof' },
  { key: 'WITH_ATTACHMENTS', label: 'With attachments' },
  { key: 'RECENT', label: 'Recently updated' },
  { key: 'COMPLETED', label: 'Completed' },
  { key: 'MENTIONS', label: 'Mentions me' },
  { key: 'PREVIOUSLY_MINE', label: 'Previously assigned to me' },
]

const STATUS_FILTER_OPTIONS: ChecklistWorkflowStatus[] = [
  'ASSIGNED',
  'ACCEPTED',
  'ONGOING',
  'ASSESSING',
  'COMPLETED',
  'POSTPONED',
  'NOT_REQUIRED',
]

const BOARD_FILTER_OPTIONS: BoardFilter[] = ['DASHBOARD', 'ARCHIVE', 'BIN']

const SORT_OPTIONS: Array<{ value: SortMode; label: string }> = [
  { value: 'DUE_ASC', label: 'Due date (soonest)' },
  { value: 'DUE_DESC', label: 'Due date (latest)' },
  { value: 'UPDATED_DESC', label: 'Recently updated' },
  { value: 'URGENCY_DESC', label: 'Urgency' },
  { value: 'IMPORTANCE_DESC', label: 'Importance' },
  { value: 'STATUS', label: 'Workflow status' },
  { value: 'ASSIGNEE', label: 'Assignee' },
  { value: 'TITLE', label: 'Title' },
]

const VISIBILITY_OPTIONS: Array<{ value: VisibilityScope; label: string }> = [
  { value: 'MINE', label: 'My work' },
  { value: 'ASSIGNED', label: 'Team work' },
]

const STATUS_SORT_ORDER: Record<ChecklistWorkflowStatus, number> = {
  ASSIGNED: 1,
  ACCEPTED: 2,
  ONGOING: 3,
  ASSESSING: 4,
  POSTPONED: 5,
  COMPLETED: 6,
  NOT_REQUIRED: 7,
  CREATED: 8,
}

const resolveMemberDisplayName = (data: Record<string, unknown>): string => {
  const candidates = [data.displayName, data.email, data.phone]
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim()
  }
  return 'Workspace member'
}

const toDate = (value: unknown): Date | null => {
  if (value instanceof Date) return value
  if (value && typeof value === 'object' && 'toDate' in value) {
    try { return (value as { toDate: () => Date }).toDate() } catch { return null }
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date
  }
  return null
}

const formatDate = (value: unknown, fallback = 'Not scheduled') => {
  const date = toDate(value)
  return date ? date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : fallback
}

const formatStatusLabel = (status: ChecklistWorkflowStatus) =>
  status
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

const statusOf = normalizeMemberForYouStatus
const boardOf = (item: ChecklistItem): BoardFilter => resolveMemberForYouBoard(item)

const dueMs = (item: ChecklistItem): number | null => {
  const raw = item.scheduleEndDate ?? item.scheduleStartDate
  if (!raw) return null
  const parsed = Date.parse(raw)
  return Number.isNaN(parsed) ? null : parsed
}

const updatedMs = (item: ChecklistItem): number | null => {
  const date = toDate(item.updatedAt)
  return date ? date.getTime() : null
}

const hasProof = (item: ChecklistItem): boolean =>
  Boolean(item.proofFileUrl || item.proofSubmissionId)

const hasMemo = (item: ChecklistItem): boolean =>
  Boolean(item.voiceMemoBlobUrl || item.proofVideoUrl || item.memoStoragePath)

const hasSurfaceAttachments = (item: ChecklistItem): boolean =>
  hasProof(item)
  || hasMemo(item)
  || (item.commentsCount ?? 0) > 0
  || (item.attachedFormIds?.length ?? 0) > 0
  || (item.attachedDesignIds?.length ?? 0) > 0

const getDueTone = (item: ChecklistItem) => {
  const value = dueMs(item)
  if (value == null) return { label: 'No due date', tone: 'muted' as const }
  const now = Date.now()
  const diff = value - now
  if (diff < 0) return { label: 'Overdue', tone: 'danger' as const }
  if (diff <= 24 * 60 * 60 * 1000) return { label: 'Today', tone: 'warn' as const }
  if (diff <= 7 * 24 * 60 * 60 * 1000) return { label: 'Due soon', tone: 'info' as const }
  return {
    label: new Date(value).toLocaleDateString([], { month: 'short', day: 'numeric' }),
    tone: 'muted' as const,
  }
}

const compareStrings = (left: string, right: string) => left.localeCompare(right, undefined, { sensitivity: 'base' })

const buildAttachmentEntries = (
  item: ChecklistItem,
  proofs: HelperChecklistProof[],
  comments: HelperChecklistComment[],
): AttachmentEntry[] => {
  const entries: AttachmentEntry[] = []
  for (const proof of proofs) {
    if (!proof.fileUrl) continue
    entries.push({
      id: `proof:${proof.id}`,
      kind: 'PROOF',
      name: proof.fileName ?? 'Proof attachment',
      url: proof.fileUrl,
      meta: [proof.uploadedByName, formatDate(proof.uploadedAt)].filter(Boolean).join(' · '),
    })
  }
  if (item.voiceMemoBlobUrl || item.proofVideoUrl) {
    entries.push({
      id: `memo:${item.id}`,
      kind: 'MEMO',
      name: item.memoFileName ?? (item.proofVideoUrl ? 'Video memo' : 'Voice memo'),
      url: item.proofVideoUrl ?? item.voiceMemoBlobUrl ?? '',
      meta: 'Execution memo',
    })
  }
  for (const comment of comments) {
    for (const attachment of comment.attachments) {
      if (!attachment.downloadUrl) continue
      entries.push({
        id: `comment:${comment.id}:${attachment.id}`,
        kind: 'COMMENT',
        name: attachment.name,
        url: attachment.downloadUrl,
        meta: [comment.actorName, formatDate(comment.createdAt)].filter(Boolean).join(' · '),
      })
    }
  }
  return entries
}

export const extractMentionedItemCounts = (notifications: NotificationRecord[], workspaceId: string): Map<string, number> => {
  const counts = new Map<string, number>()
  for (const notification of notifications) {
    if (notification.workspaceId !== workspaceId || notification.eventType !== 'MENTION_CREATED') continue
    const itemId =
      (typeof notification.data?.checklistItemId === 'string' && notification.data.checklistItemId)
      || (typeof notification.data?.itemId === 'string' && notification.data.itemId)
      || (typeof notification.entityId === 'string' && notification.entityId)
    if (!itemId) continue
    counts.set(itemId, (counts.get(itemId) ?? 0) + 1)
  }
  return counts
}

export const ForYouPage = ({ onOpenRaid }: { onOpenRaid?: () => void }) => {
  const { t } = useNls()
  const { currentUser } = useAuth()
  const currentDisplayName = useDisplayName(currentUser)
  const { activeWorkspaceId, canPerform, workspaces } = useWorkspace()
  const { workspaceId, elements, applyStripboardDelta, canPerformOnNode } = useTimeline()
  const { activeRaid } = useRaid()
  const { notifications } = useNotifications()
  const { timeZone, isDifferentFromDevice, workspaceDayLabel, todayIso, weekKey } = useWorkspaceDay(workspaceId || activeWorkspaceId)
  const [items, setItems] = useState<ChecklistItem[]>([])
  const [members, setMembers] = useState<MemberOption[]>([])
  const [formRequests, setFormRequests] = useState<FormRequest[]>([])
  const [activeFormRequest, setActiveFormRequest] = useState<FormRequest | null>(null)
  const [selectedItemId, setSelectedItemId] = usePersistedState<string | null>('forYou.selectedItemId.v2', null)
  const [visibilityScope, setVisibilityScope] = usePersistedState<VisibilityScope>('forYou.visibilityScope.v2', 'MINE')
  const [sort, setSort] = usePersistedState<SortMode>('forYou.sort.v2', 'DUE_ASC')
  const [searchText, setSearchText] = usePersistedState<string>('forYou.search.v2', '')
  const [quickFilters, setQuickFilters] = usePersistedState<QuickFilterKey[]>('forYou.quickFilters.v2', [])
  const [advancedFilters, setAdvancedFilters] = usePersistedState<AdvancedFilters>('forYou.advancedFilters.v2', DEFAULT_ADVANCED_FILTERS)
  const [showAdvancedFilters, setShowAdvancedFilters] = usePersistedState<boolean>('forYou.showAdvancedFilters.v2', false)
  const [selectedComments, setSelectedComments] = useState<HelperChecklistComment[]>([])
  const [selectedProofs, setSelectedProofs] = useState<HelperChecklistProof[]>([])
  const [selectedActivity, setSelectedActivity] = useState<ActivityEntry[]>([])
  const [busyAction, setBusyAction] = useState<string | null>(null)
  const [actionMessage, setActionMessage] = useState<{ tone: 'success' | 'error'; body: string } | null>(null)
  const [notNeededScopeDialogOpen, setNotNeededScopeDialogOpen] = useState(false)
  const [notNeededScopeChoice, setNotNeededScopeChoice] = useState<'THIS_PERIOD' | 'FROM_PERIOD'>('THIS_PERIOD')
  const proofInputRef = useRef<HTMLInputElement | null>(null)
  const activeFormDefinition = useFormDefinition(activeFormRequest?.formId ?? null)
  const activeWorkspace = workspaces.find((workspace) => workspace.id === activeWorkspaceId || workspace.id === workspaceId)
  const formsOn = isModuleEnabled(activeWorkspace, 'forms')
  const blockedTodoPolicy = normalizeChecklistBlockedTodoPolicy(activeWorkspace)
  const actor = currentUser
    ? { id: currentUser.uid, name: currentDisplayName || currentUser.email || 'Teammate' }
    : null

  useEffect(() => {
    if (!workspaceId) return
    const unsubscribers = [
      subscribeChecklistItemsView({ workspaceId }, (nextItems) => setItems(nextItems.filter((item) => !isChecklistGate(item)))),
      onSnapshot(query(collection(firestoreDb, 'workspace_members'), where('workspaceId', '==', workspaceId)), (snapshot) => {
        setMembers(snapshot.docs.map((memberDoc) => {
          const data = memberDoc.data() as Record<string, unknown>
          if (typeof data.status === 'string' && data.status !== 'ACTIVE') return null
          const userId = typeof data.uid === 'string' ? data.uid : typeof data.userId === 'string' ? data.userId : ''
          const displayName = resolveMemberDisplayName(data)
          return { userId, displayName }
        }).filter((member): member is MemberOption => Boolean(member?.userId)))
      }),
    ]
    return () => { unsubscribers.forEach((unsubscribe) => unsubscribe()) }
  }, [workspaceId])

  useEffect(() => {
    if (!workspaceId || !currentUser || !formsOn) return
    return subscribeAssignedFormRequests(workspaceId, currentUser.uid, setFormRequests, (error) => setActionMessage({ tone: 'error', body: error.message }))
  }, [currentUser, formsOn, workspaceId])

  const parentById = useMemo(() => new Map(elements.map((element) => [element.id, element])), [elements])
  const memberMap = useMemo(() => new Map(members.map((member) => [member.userId, member.displayName])), [members])
  const itemById = useMemo(() => new Map(items.map((item) => [item.id, item])), [items])
  const mentionedItemCounts = useMemo(() => extractMentionedItemCounts(notifications, workspaceId), [notifications, workspaceId])

  const pendingFormRequests = useMemo(
    () => formRequests.filter((request) => request.status === 'PENDING'),
    [formRequests],
  )

  const filteredItems = useMemo(() => {
    const search = searchText.trim().toLowerCase()
    const now = Date.now()
    const filtered = items.filter((item) => {
      const currentStatus = statusOf(item)
      const currentBoard = boardOf(item)
      const rowRecurringMode = item.checklistType === 'DAILY' || item.checklistType === 'WEEKLY' ? item.checklistType : null
      const targetPeriodKey = rowRecurringMode ? (occurrenceKeyByItemId[item.id] ?? currentOccurrenceKeyForItem(item, new Date(), timeZone)) : todayIso
      const dependencyState = getChecklistDependencyBlockState(item, itemById, targetPeriodKey)
      const itemDue = dueMs(item)
      const itemUpdated = updatedMs(item)
      const itemMentions = mentionedItemCounts.get(item.id) ?? 0
      const isMine = Boolean(currentUser && item.assigneeId === currentUser.uid)
      const isPreviouslyMine = Boolean(currentUser && item.assigneeHistoryIds?.includes(currentUser.uid) && item.assigneeId !== currentUser.uid)

      const isExplicitlyLookingForBlocked = advancedFilters.dependency === 'BLOCKED' || quickFilters.includes('BLOCKED')
      if (dependencyState.isBlocked && !isExplicitlyLookingForBlocked) return false

      if (visibilityScope === 'MINE' && !isMine) return false

      if (advancedFilters.assigneeIds.length > 0) {
        const assigneeKey = item.assigneeId ?? 'UNASSIGNED'
        if (!advancedFilters.assigneeIds.includes(assigneeKey)) return false
      }
      if (advancedFilters.creatorIds.length > 0) {
        const creatorKey = item.createdById ?? ''
        if (!advancedFilters.creatorIds.includes(creatorKey)) return false
      }
      if (advancedFilters.statuses.length > 0 && !advancedFilters.statuses.includes(currentStatus)) return false
      if (advancedFilters.boards.length > 0 && !advancedFilters.boards.includes(currentBoard)) return false
      if (advancedFilters.dependency === 'BLOCKED' && !dependencyState.isBlocked) return false
      if (advancedFilters.dependency === 'CLEAR' && dependencyState.isBlocked) return false
      if (advancedFilters.proof === 'REQUIRED' && !item.requiresProofFile) return false
      if (advancedFilters.proof === 'MISSING_REQUIRED' && (!item.requiresProofFile || hasProof(item))) return false
      if (advancedFilters.proof === 'UPLOADED' && !hasProof(item)) return false
      if (advancedFilters.attachment === 'HAS' && !hasSurfaceAttachments(item)) return false
      if (advancedFilters.attachment === 'NONE' && hasSurfaceAttachments(item)) return false
      if (formsOn && advancedFilters.forms === 'HAS' && (item.attachedFormIds?.length ?? 0) === 0) return false
      if (formsOn && advancedFilters.forms === 'NONE' && (item.attachedFormIds?.length ?? 0) > 0) return false
      if (advancedFilters.updatedWindow === '24H' && (!itemUpdated || itemUpdated < now - 24 * 60 * 60 * 1000)) return false
      if (advancedFilters.updatedWindow === '7D' && (!itemUpdated || itemUpdated < now - 7 * 24 * 60 * 60 * 1000)) return false
      if (advancedFilters.updatedWindow === '30D' && (!itemUpdated || itemUpdated < now - 30 * 24 * 60 * 60 * 1000)) return false

      if (advancedFilters.dueWindow === 'NO_DATE' && itemDue !== null) return false
      if (advancedFilters.dueWindow === 'OVERDUE' && !(itemDue !== null && itemDue < now)) return false
      if (advancedFilters.dueWindow === 'TODAY' && (dependencyState.isBlocked || !(itemDue !== null && itemDue >= now && itemDue <= now + 24 * 60 * 60 * 1000))) return false
      if (advancedFilters.dueWindow === 'THIS_WEEK' && !(itemDue !== null && itemDue >= now && itemDue <= now + 7 * 24 * 60 * 60 * 1000)) return false
      if (advancedFilters.dueWindow === 'FUTURE' && !(itemDue !== null && itemDue > now + 7 * 24 * 60 * 60 * 1000)) return false

      for (const quickFilter of quickFilters) {
        if (quickFilter === 'OVERDUE' && !(itemDue !== null && itemDue < now && currentStatus !== 'COMPLETED')) return false
        if (quickFilter === 'DUE_SOON' && (dependencyState.isBlocked || !(itemDue !== null && itemDue >= now && itemDue <= now + 7 * 24 * 60 * 60 * 1000 && currentStatus !== 'COMPLETED'))) return false
        if (quickFilter === 'BLOCKED' && !dependencyState.isBlocked) return false
        if (quickFilter === 'NEEDS_PROOF' && !(item.requiresProofFile && !hasProof(item))) return false
        if (quickFilter === 'WITH_ATTACHMENTS' && !hasSurfaceAttachments(item)) return false
        if (quickFilter === 'RECENT' && !(itemUpdated !== null && itemUpdated >= now - 24 * 60 * 60 * 1000)) return false
        if (quickFilter === 'COMPLETED' && currentStatus !== 'COMPLETED') return false
        if (quickFilter === 'MENTIONS' && itemMentions === 0) return false
        if (quickFilter === 'PREVIOUSLY_MINE' && !isPreviouslyMine) return false
      }

      if (search) {
        const parentTitle = parentById.get(item.parentContainerId)?.title ?? ''
        const haystack = [
          item.title,
          item.description,
          item.completionCriteria,
          item.assigneeName,
          item.createdByName,
          parentTitle,
          memberMap.get(item.assigneeId ?? ''),
        ]
          .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
          .join(' ')
          .toLowerCase()
        if (!haystack.includes(search)) return false
      }

      return true
    })

    return [...filtered].sort((left, right) => {
      const leftStatus = statusOf(left)
      const rightStatus = statusOf(right)
      if (sort === 'DUE_ASC') return (dueMs(left) ?? Number.MAX_SAFE_INTEGER) - (dueMs(right) ?? Number.MAX_SAFE_INTEGER)
      if (sort === 'DUE_DESC') return (dueMs(right) ?? -1) - (dueMs(left) ?? -1)
      if (sort === 'UPDATED_DESC') return (updatedMs(right) ?? 0) - (updatedMs(left) ?? 0)
      if (sort === 'URGENCY_DESC') return (right.urgencyStars ?? 0) - (left.urgencyStars ?? 0)
      if (sort === 'IMPORTANCE_DESC') return (right.importanceStars ?? 0) - (left.importanceStars ?? 0)
      if (sort === 'STATUS') return (STATUS_SORT_ORDER[leftStatus] ?? 99) - (STATUS_SORT_ORDER[rightStatus] ?? 99)
      if (sort === 'ASSIGNEE') return compareStrings(left.assigneeName ?? 'Unassigned', right.assigneeName ?? 'Unassigned')
      return compareStrings(left.title ?? '', right.title ?? '')
    })
  }, [advancedFilters, currentUser, formsOn, itemById, items, memberMap, mentionedItemCounts, parentById, quickFilters, searchText, sort, visibilityScope])

  useEffect(() => {
    if (!selectedItemId && filteredItems.length > 0) {
      setSelectedItemId(filteredItems[0].id)
      return
    }
    if (selectedItemId && filteredItems.some((item) => item.id === selectedItemId)) return
    setSelectedItemId(filteredItems[0]?.id ?? null)
  }, [filteredItems, selectedItemId, setSelectedItemId])

  const listSelectedItem = useMemo(
    () => filteredItems.find((item) => item.id === selectedItemId) ?? null,
    [filteredItems, selectedItemId],
  )

  // ------------------------------------------------------------------------
  // Recurring instance switcher — DAILY/WEEKLY items get a per-selection
  // occurrence key so users can move between instances inside the details
  // panel without leaving Todos. Aggregate list stays single-card per
  // definition; the switcher only lives inside the inspector. Individual
  // items ignore this and behave as before.
  // ------------------------------------------------------------------------
  const [occurrenceKeyByItemId, setOccurrenceKeyByItemId] = useState<Record<string, string>>({})
  const listSelectedRecurringMode: 'DAILY' | 'WEEKLY' | null =
    listSelectedItem?.checklistType === 'DAILY' || listSelectedItem?.checklistType === 'WEEKLY'
      ? listSelectedItem.checklistType
      : null
  const activeOccurrenceKey = listSelectedItem && listSelectedRecurringMode
    ? (occurrenceKeyByItemId[listSelectedItem.id]
        ?? currentOccurrenceKeyForItem(listSelectedItem, new Date(), timeZone))
    : null
  const isViewingToday = !listSelectedRecurringMode
    || activeOccurrenceKey === (listSelectedItem ? currentOccurrenceKeyForItem(listSelectedItem, new Date(), timeZone) : null)

  // For non-today occurrences, subscribe to the per-instance projection so
  // status / proof / schedule reflect that specific day/week. Today's data
  // is already in the aggregate feed, so we skip the extra subscription
  // there.
  const [projectedOccurrenceItem, setProjectedOccurrenceItem] = useState<ChecklistItem | null>(null)
  useEffect(() => {
    if (!listSelectedItem || !listSelectedRecurringMode || isViewingToday || !activeOccurrenceKey) {
      setProjectedOccurrenceItem(null)
      return
    }
    const unsubscribe = subscribeChecklistItemProjection(
      { itemId: listSelectedItem.id, pageKey: activeOccurrenceKey },
      (next) => setProjectedOccurrenceItem(next),
    )
    return unsubscribe
  }, [activeOccurrenceKey, isViewingToday, listSelectedItem, listSelectedRecurringMode])

  const selectedItem: ChecklistItem | null = listSelectedRecurringMode && !isViewingToday
    ? (projectedOccurrenceItem ?? listSelectedItem)
    : listSelectedItem
  const selectedPageKey = listSelectedRecurringMode ? activeOccurrenceKey : null

  const setOccurrenceKey = (nextKey: string) => {
    if (!listSelectedItem) return
    setOccurrenceKeyByItemId((current) => ({ ...current, [listSelectedItem.id]: nextKey }))
  }
  const clampedOccurrenceValue = listSelectedRecurringMode === 'WEEKLY'
    ? (activeOccurrenceKey ?? '')
    : (activeOccurrenceKey ?? '')

  useEffect(() => {
    if (!selectedItem?.id) {
      setSelectedComments([])
      setSelectedProofs([])
      return
    }
    const itemWorkspaceId = selectedItem.workspaceId || workspaceId || activeWorkspaceId || ''
    const stops = [
      subscribeChecklistComments(selectedItem.id, itemWorkspaceId, setSelectedComments),
      subscribeChecklistProofs(selectedItem.id, setSelectedProofs),
    ]
    return () => { stops.forEach((stop) => stop()) }
  }, [activeWorkspaceId, selectedItem?.id, selectedItem?.workspaceId, workspaceId])


  useEffect(() => {
    if (!workspaceId || !selectedItem?.id) {
      setSelectedActivity([])
      return
    }
    let cancelled = false
    const now = new Date()
    void queryWorkspaceActivityPage(
      firestoreDb,
      workspaceId,
      {
        from: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
        to: now,
        memberIds: [],
        itemIds: [selectedItem.id],
        activityTypes: [],
      },
      null,
      30,
    )
      .then((result) => {
        if (cancelled) return
        setSelectedActivity(result.records.map((record) => ({
          id: record.id,
          summary: record.summary || record.action || 'Updated this item',
          actorName: record.actorName || 'Teammate',
          createdAt: record.createdAt,
        })))
      })
      .catch(() => {
        if (!cancelled) setSelectedActivity([])
      })
    return () => { cancelled = true }
  }, [selectedItem?.id, workspaceId])

  const selectedStatus = selectedItem ? statusOf(selectedItem) : null
  const selectedAllowedTransitions = useMemo(() => {
    if (!selectedItem) return []
    return resolveAllowedTransitions({
      workflowStatus: selectedStatus,
      allowedTransitions: (selectedItem as ChecklistItem & {
        allowedTransitions?: Partial<Record<ChecklistWorkflowStatus, ChecklistWorkflowStatus[]>> | null
      }).allowedTransitions ?? null,
    })
  }, [selectedItem, selectedStatus])

  const selectedDependencyState = useMemo(
    () => (selectedItem ? getChecklistDependencyBlockState(selectedItem, itemById, activeOccurrenceKey ?? (selectedItem.checklistType === 'WEEKLY' ? weekKey : todayIso)) : { isBlocked: false, reasons: [], blockerCount: 0 }),
    [activeOccurrenceKey, itemById, selectedItem, todayIso, weekKey],
  )

  const selectedActionPolicy = useMemo(
    () => resolveMemberForYouActionPolicy(selectedItem, currentUser?.uid, {
      canChangeStatus: selectedItem ? canPerformOnNode('checklist.change_status', selectedItem.id) : canPerform('checklist.change_status'),
      canManageProof: selectedItem ? canPerformOnNode('checklist.manage_proof', selectedItem.id) : canPerform('checklist.manage_proof'),
    }),
    [canPerform, canPerformOnNode, currentUser?.uid, selectedItem],
  )
  const canActOnSelectedItem = selectedActionPolicy.canActOnItem
  const canUpdateStatus = selectedActionPolicy.canUpdateStatus
  const canUploadProof = selectedActionPolicy.canUploadProof
  const attachments = useMemo(
    () => (selectedItem ? buildAttachmentEntries(selectedItem, selectedProofs, selectedComments) : []),
    [selectedComments, selectedItem, selectedProofs],
  )

  const metrics = useMemo(() => {
    const workspaceVisible = items.filter((item) => boardOf(item) === 'DASHBOARD')
    const myOpen = workspaceVisible.filter((item) => currentUser && item.assigneeId === currentUser.uid && statusOf(item) !== 'COMPLETED').length
    const dueSoon = workspaceVisible.filter((item) => {
      const value = dueMs(item)
      return value !== null && value >= Date.now() && value <= Date.now() + 7 * 24 * 60 * 60 * 1000 && statusOf(item) !== 'COMPLETED'
    }).length
    const blocked = workspaceVisible.filter((item) => getChecklistDependencyBlockState(item, itemById).isBlocked).length
    const completed = workspaceVisible.filter((item) => statusOf(item) === 'COMPLETED').length
    return { myOpen, dueSoon, blocked, completed }
  }, [currentUser, itemById, items])

  const activeFilterSummary = useMemo(() => {
    const summary: string[] = []
    summary.push(VISIBILITY_OPTIONS.find((option) => option.value === visibilityScope)?.label ?? 'My work')
    if (searchText.trim()) summary.push(`Search: "${searchText.trim()}"`)
    if (quickFilters.length > 0) summary.push(...quickFilters.map((filter) => QUICK_FILTERS.find((option) => option.key === filter)?.label ?? filter))
    if (advancedFilters.assigneeIds.length > 0) summary.push(`Assignee: ${advancedFilters.assigneeIds.length}`)
    if (advancedFilters.creatorIds.length > 0) summary.push(`Creator: ${advancedFilters.creatorIds.length}`)
    if (advancedFilters.statuses.length > 0) summary.push(`Statuses: ${advancedFilters.statuses.length}`)
    if (advancedFilters.boards.length > 0) summary.push(`Boards: ${advancedFilters.boards.length}`)
    if (advancedFilters.dueWindow !== 'ANY') summary.push(`Due: ${advancedFilters.dueWindow.replaceAll('_', ' ').toLowerCase()}`)
    if (advancedFilters.dependency !== 'ANY') summary.push(advancedFilters.dependency === 'BLOCKED' ? 'Blocked only' : 'Dependency clear')
    if (advancedFilters.proof !== 'ANY') summary.push(`Proof: ${advancedFilters.proof.replaceAll('_', ' ').toLowerCase()}`)
    if (advancedFilters.attachment !== 'ANY') summary.push(advancedFilters.attachment === 'HAS' ? 'Has attachments' : 'No attachments')
    if (advancedFilters.forms !== 'ANY') summary.push(advancedFilters.forms === 'HAS' ? 'Has forms' : 'No forms')
    if (advancedFilters.updatedWindow !== 'ANY') summary.push(`Updated: ${advancedFilters.updatedWindow}`)
    return summary
  }, [advancedFilters, quickFilters, searchText, visibilityScope])

  const resetFilters = () => {
    setVisibilityScope('MINE')
    setQuickFilters([])
    setSearchText('')
    setAdvancedFilters(DEFAULT_ADVANCED_FILTERS)
  }

  const transitionSelectedItem = async (
    next: ChecklistWorkflowStatus,
    opts?: { notRequiredScope?: 'FROM_PERIOD' | 'CLEAR' | null; skipScopePrompt?: boolean },
  ) => {
    if (!selectedItem || !selectedStatus || !actor) return
    if (!canUpdateStatus) return
    if (selectedDependencyState.isBlocked && (next === 'COMPLETED' ? !blockedTodoPolicy.allowCompletion : !blockedTodoPolicy.allowStatusChange)) {
      setActionMessage({
        tone: 'error',
        body: next === 'COMPLETED' ? 'Blocked by dependency. Completion is disabled here.' : 'Blocked by dependency. Status changes are disabled here.',
      })
      return
    }
    const isRecurringSelected = selectedItem.checklistType === 'DAILY' || selectedItem.checklistType === 'WEEKLY'
    if (
      next === 'NOT_REQUIRED'
      && isRecurringSelected
      && !opts?.skipScopePrompt
    ) {
      setNotNeededScopeChoice('THIS_PERIOD')
      setNotNeededScopeDialogOpen(true)
      return
    }
    const effectiveNotRequiredScope =
      opts?.notRequiredScope !== undefined
        ? opts.notRequiredScope
        : next !== 'NOT_REQUIRED' && selectedItem.notRequiredFrom
          ? 'CLEAR'
          : null
    setBusyAction(`status:${next}`)
    setActionMessage(null)
    try {
      const coordinates = next === 'COMPLETED' && selectedItem.requiresLocationPin && !selectedItem.verifiedCoordinates
        ? await readCurrentCoordinates()
        : null
      if (next === 'COMPLETED' && selectedItem.requiresLocationPin && !coordinates && !selectedItem.verifiedCoordinates) {
        throw new Error('Location check-in is required before this can be completed.')
      }
      await updateChecklistWorkflowStatus({
        item: {
          id: selectedItem.id,
          boardStatus: selectedItem.boardStatus,
          recordVersion: selectedItem.recordVersion,
          workflowStatus: selectedStatus,
          workspaceId: selectedItem.workspaceId,
          checklistType: selectedItem.checklistType,
        },
        workflowStatus: next,
        coordinates,
        actor,
        runDelta: applyStripboardDelta,
        periodKey: isRecurringSelected ? (selectedPageKey ?? null) : null,
        periodBoardStatus: isRecurringSelected && selectedPageKey ? selectedItem.occurrences?.[selectedPageKey]?.boardStatus : undefined,
        pageKey: selectedPageKey ?? null,
        notRequiredScope: effectiveNotRequiredScope,
      })
      setActionMessage({ tone: 'success', body: `Marked as ${formatStatusLabel(next).toLowerCase()}.` })
    } catch (error) {
      setActionMessage({ tone: 'error', body: error instanceof Error ? error.message : 'Could not update this item.' })
    } finally {
      setBusyAction(null)
    }
  }

  const uploadSelectedProof = async (file: File) => {
    if (!selectedItem || !actor || !canUploadProof) return
    if (selectedDependencyState.isBlocked && !blockedTodoPolicy.allowProof) {
      setActionMessage({ tone: 'error', body: 'Blocked by dependency. Proof upload is disabled here.' })
      return
    }
    setBusyAction('proof')
    setActionMessage(null)
    try {
      const attachment = await uploadChecklistProof({
        workspaceId,
        checklistItemId: selectedItem.id,
        pageKey: selectedPageKey ?? null,
        file,
        fileName: file.name,
        kind: 'PROOF',
        identity: { userId: actor.id, userName: actor.name },
      })
      const proofUpdates = buildChecklistProofAttachmentUpdates(attachment)
      await updateChecklistFields(firestoreDb, {
        item: selectedItem,
        updates: proofUpdates,
        actor,
        pageKey: selectedPageKey ?? null,
        action: 'CHECKLIST_PROOF_UPDATED',
        summary: 'Attached completion proof',
        metadata: { changedFields: Object.keys(proofUpdates), proofSubmissionId: attachment.proofSubmissionId ?? attachment.id },
      })
      setActionMessage({ tone: 'success', body: `Uploaded proof: ${file.name}.` })
    } catch (error) {
      setActionMessage({ tone: 'error', body: error instanceof Error ? error.message : 'Could not upload proof.' })
    } finally {
      setBusyAction(null)
    }
  }

  return (
    <RailPageShell
      variant="two-column"
      ariaLabel="Todos"
      className="forYouPage"
      collapsible={false}
      identity={{
        badge: 'T',
        title: 'Todos',
        subtitle: isDifferentFromDevice
          ? t('NLSChecklistWorkspaceDayLabel', { day: workspaceDayLabel })
          : 'Execution queue',
      }}
      columnSizes={{ left: '340px' }}
      leftRail={(
        <section className="forYouMetrics forYouMetrics--vertical">
          <Card variant="glass" accent="primary" eyebrow="My queue" title={`${metrics.myOpen}`} subtitle="Open items currently assigned to you" icon={<TaskAltRoundedIcon fontSize="inherit" />} />
          <Card variant="glass" accent="warn" eyebrow="Due soon" title={`${metrics.dueSoon}`} subtitle="Visible checklist items due in the next 7 days" icon={<ScheduleRoundedIcon fontSize="inherit" />} />
          <Card variant="glass" accent="danger" eyebrow="Blocked" title={`${metrics.blocked}`} subtitle="Visible items currently blocked by dependencies" icon={<WarningAmberRoundedIcon fontSize="inherit" />} />
          <Card variant="glass" accent="success" eyebrow="Completed" title={`${metrics.completed}`} subtitle="Visible items already completed in this workspace" icon={<CheckCircleRoundedIcon fontSize="inherit" />} />
          <Card variant="glass" accent="primary" eyebrow="Mentions" title={`${Array.from(mentionedItemCounts.values()).reduce((sum, count) => sum + count, 0)}`} subtitle="Checklist mentions tied to your notifications" icon={<AlternateEmailRoundedIcon fontSize="inherit" />} />
          {formsOn && (
            <Card variant="glass" accent="primary" eyebrow="Assigned forms" title={`${pendingFormRequests.length}`} subtitle="Pending forms requested directly from you" icon={<AssignmentIndRoundedIcon fontSize="inherit" />} />
          )}
        </section>
      )}
      rightStage={(
        <>
      <section className="forYouControlDeck">
        <div className="forYouPrimaryControls">
          <div className="settingsSegmented forYouScopeSwitch" role="group" aria-label="Visibility scope">
            {VISIBILITY_OPTIONS.map((option) => (
              <Pressable
                key={option.value}
                className={visibilityScope === option.value ? 'isActive' : ''}
                aria-pressed={visibilityScope === option.value}
                onClick={() => setVisibilityScope(option.value)}
              >
                {option.value === 'MINE' ? <PersonRoundedIcon fontSize="small" /> : <GroupsRoundedIcon fontSize="small" />}
                {option.label}
              </Pressable>
            ))}
          </div>

          <label className="forYouSearchField">
            <TextInput
              type="search"
              leadingIcon={<SearchRoundedIcon fontSize="small" />}
              value={searchText}
              onChange={setSearchText}
              placeholder="Search title, parent, assignee, description…"
              aria-label="Search title, parent, assignee, description…"
            />
          </label>

          <TopHeaderSelect<SortMode>
            label="Sort"
            ariaLabel="Sort todos"
            value={sort}
            options={SORT_OPTIONS}
            onChange={setSort}
            className="forYouSortControl"
          />

          <AdaptiveMultiSelectFilter<QuickFilterKey>
            label="Quick filters"
            summaryLabel="quick filter"
            collapseMode="dropdown"
            options={QUICK_FILTERS.map((filter) => ({ value: filter.key, label: filter.label }))}
            selectedValues={quickFilters}
            onChange={setQuickFilters}
            className="forYouQuickFiltersControl"
          />

          <Button
            variant={showAdvancedFilters ? 'secondary' : 'tertiary'}
            icon={<TuneRoundedIcon />}
            onClick={() => setShowAdvancedFilters((current) => !current)}
          >
            Filters
          </Button>
        </div>

        {showAdvancedFilters ? (
          <div className="forYouAdvancedFilters">
            <div className="forYouAdvancedRow forYouAdvancedRow--cols-4">
              <AdaptiveMultiSelectFilter<ChecklistWorkflowStatus>
                label="Statuses"
                summaryLabel="status"
                collapseMode="dropdown"
                options={STATUS_FILTER_OPTIONS.map((status) => ({ value: status, label: formatStatusLabel(status) }))}
                selectedValues={advancedFilters.statuses}
                onChange={(next) => setAdvancedFilters((current) => ({ ...current, statuses: next }))}
              />
              <AdaptiveMultiSelectFilter<BoardFilter>
                label="Boards"
                summaryLabel="board"
                collapseMode="dropdown"
                options={BOARD_FILTER_OPTIONS.map((board) => ({
                  value: board,
                  label: board === 'DASHBOARD' ? 'Dashboard' : board === 'ARCHIVE' ? 'Archive' : 'Bin',
                }))}
                selectedValues={advancedFilters.boards}
                onChange={(next) => setAdvancedFilters((current) => ({ ...current, boards: next }))}
              />
              <AdaptiveMultiSelectFilter<string>
                label="Assignee"
                summaryLabel="assignee"
                collapseMode="dropdown"
                options={[
                  { value: 'UNASSIGNED', label: 'Unassigned' },
                  ...members.map((member) => ({ value: member.userId, label: member.displayName })),
                ]}
                selectedValues={advancedFilters.assigneeIds}
                onChange={(next) => setAdvancedFilters((current) => ({ ...current, assigneeIds: next }))}
              />
              <AdaptiveMultiSelectFilter<string>
                label="Created by"
                summaryLabel="creator"
                collapseMode="dropdown"
                options={members.map((member) => ({ value: member.userId, label: member.displayName }))}
                selectedValues={advancedFilters.creatorIds}
                onChange={(next) => setAdvancedFilters((current) => ({ ...current, creatorIds: next }))}
              />
            </div>

            <div className="forYouAdvancedRow forYouAdvancedRow--cols-3">
              <TopHeaderSelect<DueWindow>
                label="Due window"
                ariaLabel="Due window filter"
                value={advancedFilters.dueWindow}
                options={[
                  { value: 'ANY', label: 'Any due date' },
                  { value: 'OVERDUE', label: 'Overdue' },
                  { value: 'TODAY', label: 'Today' },
                  { value: 'THIS_WEEK', label: 'This week' },
                  { value: 'FUTURE', label: 'Later than 7 days' },
                  { value: 'NO_DATE', label: 'No due date' },
                ]}
                onChange={(value) => setAdvancedFilters((current) => ({ ...current, dueWindow: value }))}
              />
              <TopHeaderSelect<DependencyFilter>
                label="Dependency"
                ariaLabel="Dependency filter"
                value={advancedFilters.dependency}
                options={[
                  { value: 'ANY', label: 'Any dependency state' },
                  { value: 'BLOCKED', label: 'Blocked only' },
                  { value: 'CLEAR', label: 'Dependency clear' },
                ]}
                onChange={(value) => setAdvancedFilters((current) => ({ ...current, dependency: value }))}
              />
              <TopHeaderSelect<ProofFilter>
                label="Proof"
                ariaLabel="Proof filter"
                value={advancedFilters.proof}
                options={[
                  { value: 'ANY', label: 'Any proof state' },
                  { value: 'REQUIRED', label: 'Proof required' },
                  { value: 'MISSING_REQUIRED', label: 'Missing required proof' },
                  { value: 'UPLOADED', label: 'Proof uploaded' },
                ]}
                onChange={(value) => setAdvancedFilters((current) => ({ ...current, proof: value }))}
              />
            </div>

            <div className="forYouAdvancedRow forYouAdvancedRow--cols-3">
              <TopHeaderSelect<AttachmentFilter>
                label="Attachments"
                ariaLabel="Attachment filter"
                value={advancedFilters.attachment}
                options={[
                  { value: 'ANY', label: 'Any attachment state' },
                  { value: 'HAS', label: 'Has attachments' },
                  { value: 'NONE', label: 'No attachments' },
                ]}
                onChange={(value) => setAdvancedFilters((current) => ({ ...current, attachment: value }))}
              />
              {formsOn && (
                <TopHeaderSelect<FormFilter>
                  label="Forms"
                  ariaLabel="Attached forms filter"
                  value={advancedFilters.forms}
                  options={[
                    { value: 'ANY', label: 'Any form state' },
                    { value: 'HAS', label: 'Has attached forms' },
                    { value: 'NONE', label: 'No attached forms' },
                  ]}
                  onChange={(value) => setAdvancedFilters((current) => ({ ...current, forms: value }))}
                />
              )}
              <TopHeaderSelect<UpdatedWindow>
                label="Updated"
                ariaLabel="Updated filter"
                value={advancedFilters.updatedWindow}
                options={[
                  { value: 'ANY', label: 'Any update window' },
                  { value: '24H', label: 'Last 24 hours' },
                  { value: '7D', label: 'Last 7 days' },
                  { value: '30D', label: 'Last 30 days' },
                ]}
                onChange={(value) => setAdvancedFilters((current) => ({ ...current, updatedWindow: value }))}
              />
            </div>
          </div>
        ) : null}

        <div className="forYouFilterSummary">
          <div>
            <span className="settingsEyebrow">Active view</span>
            <p>{activeFilterSummary.join(' · ')}</p>
          </div>
          <div className="forYouFilterSummaryActions">
            <span>{filteredItems.length} result{filteredItems.length === 1 ? '' : 's'}</span>
            {(quickFilters.length > 0 || searchText.trim() || visibilityScope !== 'MINE' || JSON.stringify(advancedFilters) !== JSON.stringify(DEFAULT_ADVANCED_FILTERS)) ? (
              <Button variant="tertiary" onClick={resetFilters}>Clear all</Button>
            ) : null}
          </div>
        </div>
      </section>

      {activeRaid && onOpenRaid ? (
        <div className="forYouRightStageActions">
          <Button variant="secondary" icon={<BoltRoundedIcon />} onClick={onOpenRaid}>
            Open Raid
          </Button>
        </div>
      ) : null}
      <div className="forYouWorkspaceShell">
        <section className="forYouQueuePanel">
          <header className="forYouPanelHeader">
            <div>
              <span className="settingsEyebrow">Execution queue</span>
              <h2>{visibilityScope === 'MINE' ? 'Your work' : 'Team work'}</h2>
            </div>
            <span>{filteredItems.length} item{filteredItems.length === 1 ? '' : 's'}</span>
          </header>

          {formsOn && pendingFormRequests.length > 0 ? (
            <div className="forYouFormRail" aria-label="Assigned forms">
              {pendingFormRequests.map((request) => (
                <Pressable
                  key={request.id}
                  className="forYouFormRailItem"
                  onClick={() => setActiveFormRequest(request)}
                >
                  <AssignmentIndRoundedIcon />
                  <span>
                    <strong>{request.formTitle}</strong>
                    <small>Requested by {request.requestedByName}</small>
                  </span>
                </Pressable>
              ))}
            </div>
          ) : null}

          <div className="forYouQueueList">
            {filteredItems.length === 0 ? (
              <div className="forYouEmptyState">
                <EmptyHubState
                  preset="forYou"
                  variant="search"
                  title="No work matches this view."
                  description="Broaden the visibility scope or clear some filters to reveal more checklist items."
                  compact
                />
              </div>
            ) : filteredItems.map((item) => {
              const currentStatus = statusOf(item)
              const rowRecurringMode: 'DAILY' | 'WEEKLY' | null =
                item.checklistType === 'DAILY' || item.checklistType === 'WEEKLY'
                  ? item.checklistType
                  : null
              const rowTargetKey = rowRecurringMode
                ? (occurrenceKeyByItemId[item.id] ?? currentOccurrenceKeyForItem(item, new Date(), timeZone))
                : todayIso
              const dependencyState = getChecklistDependencyBlockState(item, itemById, rowTargetKey ?? todayIso)
              const mentionCount = mentionedItemCounts.get(item.id) ?? 0
              const isMine = Boolean(currentUser && item.assigneeId === currentUser.uid)
              const dueTone = getDueTone(item)
              // Surface the chosen occurrence on recurring rows so the user
              // knows which instance a subsequent proof upload will attach
              // to. Hidden when targeting today / this ISO week — that's the
              // default and adding a pill for it would be noise.
              const rowTodayKey = rowRecurringMode ? currentOccurrenceKeyForItem(item, new Date(), timeZone) : null
              const rowTargetingLabel = rowRecurringMode && rowTargetKey && rowTargetKey !== rowTodayKey
                ? (rowRecurringMode === 'DAILY' ? rowTargetKey : `Week ${rowTargetKey.split('-W')[1] ?? rowTargetKey}`)
                : null
              return (
                <Pressable
                  key={item.id}
                  className={`forYouQueueRow ${selectedItemId === item.id ? 'isSelected' : ''} ${dependencyState.isBlocked ? 'isBlocked' : ''}`}
                  onClick={() => setSelectedItemId(item.id)}
                >
                  <div className="forYouQueueRowTop">
                    <span className={`forYouStatusPill is-${currentStatus.toLowerCase()}`}>{formatStatusLabel(currentStatus)}</span>
                    <span className={`forYouSignalPill is-${dueTone.tone}`}>{dueTone.label}</span>
                    {!isMine ? <span className="forYouSignalPill">View only</span> : null}
                  </div>
                  <strong>{item.title || 'Untitled checklist item'}</strong>
                  <p>{parentById.get(item.parentContainerId)?.title ?? 'Workspace checklist'} · {item.assigneeName ?? 'Unassigned'}</p>
                  <div className="forYouQueueSignals">
                    {dependencyState.isBlocked ? <span className="forYouSignalPill is-danger">Blocked</span> : null}
                    {item.requiresProofFile ? <span className="forYouSignalPill is-warn">Proof</span> : null}
                    {item.requiresLocationPin ? <span className="forYouSignalPill is-info">Location</span> : null}
                    {hasSurfaceAttachments(item) ? <span className="forYouSignalPill">Attachments</span> : null}
                    {formsOn && (item.attachedFormIds?.length ?? 0) > 0 ? <span className="forYouSignalPill">Forms</span> : null}
                    {mentionCount > 0 ? <span className="forYouSignalPill is-info">{mentionCount} mention{mentionCount === 1 ? '' : 's'}</span> : null}
                    {rowTargetingLabel ? (
                      <span className="forYouSignalPill is-info" title="Proof upload will target this occurrence">
                        Targeting {rowTargetingLabel}
                      </span>
                    ) : null}
                  </div>
                </Pressable>
              )
            })}
          </div>
        </section>

        <section className="forYouInspectorPanel">
          {selectedItem ? (
            <>
              <header className="forYouPanelHeader forYouInspectorHeader">
                <div>
                  <span className="settingsEyebrow">
                    {listSelectedRecurringMode
                      ? (listSelectedRecurringMode === 'DAILY' ? 'Selected item · Daily instance' : 'Selected item · Weekly instance')
                      : 'Selected item'}
                  </span>
                  <h2>{selectedItem.title || 'Untitled checklist item'}</h2>
                  <p>{parentById.get(selectedItem.parentContainerId)?.title ?? 'Workspace checklist'} · {selectedItem.assigneeName ?? 'Unassigned'}</p>
                </div>
                <div className="forYouInspectorHeaderMeta">
                  {listSelectedRecurringMode && listSelectedItem && (
                    <label className="forYouOccurrenceSwitcher" title={listSelectedRecurringMode === 'DAILY' ? 'Jump to another day' : 'Jump to another week'}>
                      <span>{listSelectedRecurringMode === 'DAILY' ? 'Day' : 'Week'}</span>
                      {listSelectedRecurringMode === 'DAILY' ? (
                        <DatePicker
                          value={clampedOccurrenceValue}
                          size="sm"
                          clearable={false}
                          ariaLabel="Jump to day"
                          onChange={(raw) => {
                            if (raw) setOccurrenceKey(raw)
                          }}
                        />
                      ) : (
                        // eslint-disable-next-line no-restricted-syntax -- input type="week" is unsupported by Common DatePicker
                        <input
                          type="week"
                          value={clampedOccurrenceValue}
                          onChange={(event) => {
                            const raw = event.target.value
                            if (raw) setOccurrenceKey(raw)
                          }}
                        />
                      )}
                      {!isViewingToday && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            const todayKey = currentOccurrenceKeyForItem(listSelectedItem, new Date(), timeZone)
                            setOccurrenceKey(todayKey)
                          }}
                        >
                          {listSelectedRecurringMode === 'DAILY' ? 'Today' : 'This week'}
                        </Button>
                      )}
                    </label>
                  )}
                  <span className={`forYouStatusPill is-${selectedStatus?.toLowerCase()}`}>{selectedStatus ? formatStatusLabel(selectedStatus) : 'Unknown'}</span>
                  <span className={`forYouSignalPill is-${getDueTone(selectedItem).tone}`}>{getDueTone(selectedItem).label}</span>
                </div>
              </header>

              {actionMessage ? (
                <div className={`forYouInlineMessage is-${actionMessage.tone}`}>
                  {actionMessage.body}
                </div>
              ) : null}

              <div className="forYouInspectorGrid">
                <section className="forYouInspectorCard">
                  <span className="settingsEyebrow">Execution brief</span>
                  <div className="forYouInspectorCopy">
                    <div>
                      <strong>Description</strong>
                      <p>{selectedItem.description || 'No description provided.'}</p>
                    </div>
                    <div>
                      <strong>Completion criteria</strong>
                      <p>{selectedItem.completionCriteria || 'No completion criteria provided.'}</p>
                    </div>
                  </div>
                </section>

                <section className="forYouInspectorCard">
                  <span className="settingsEyebrow">Signals</span>
                  <div className="forYouMetaList">
                    <div><strong>Assignee</strong><span>{selectedItem.assigneeName ?? 'Unassigned'}</span></div>
                    <div><strong>Created by</strong><span>{selectedItem.createdByName ?? 'Workspace teammate'}</span></div>
                    <div><strong>Schedule</strong><span>{selectedItem.scheduleMode === 'DURATION' ? `${selectedItem.scheduleStartDate || 'No start'} → ${selectedItem.scheduleEndDate || 'No end'}` : selectedItem.scheduleStartDate || 'No date selected'}</span></div>
                    <div><strong>Proof</strong><span>{selectedItem.requiresProofFile ? (hasProof(selectedItem) ? 'Uploaded' : 'Required') : 'Optional'}</span></div>
                    <div><strong>Location</strong><span>{selectedItem.requiresLocationPin ? (selectedItem.verifiedCoordinates ? 'Verified' : 'Required on completion') : 'Optional'}</span></div>
                    <div><strong>Priority</strong><span>Urgency {selectedItem.urgencyStars ?? 0}/5 · Importance {selectedItem.importanceStars ?? 0}/5</span></div>
                  </div>
                </section>

                <section className="forYouInspectorCard forYouInspectorCard--wide">
                  <span className="settingsEyebrow">Dependencies</span>
                  {selectedDependencyState.isBlocked ? (
                    <ul className="forYouReasonList">
                      {selectedDependencyState.reasons.map((reason) => (
                        <li key={`${reason.code}:${reason.targetId ?? reason.title ?? 'reason'}`}>{reason.title ?? 'Blocked by dependency'}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="forYouMuted">No dependency blockers are active for this item.</p>
                  )}
                </section>

                <section className="forYouInspectorCard forYouExecutionCard">
                  <span className="settingsEyebrow">Execution actions</span>
                  {canActOnSelectedItem ? (
                    <>
                      <p className="forYouMuted">{selectedActionPolicy.executionScopeCopy}</p>
                      <div className="forYouActionRow">
                        {selectedAllowedTransitions.length > 0 ? selectedAllowedTransitions.map((status) => (
                          <Button
                            key={status}
                            variant={status === 'COMPLETED' ? 'primary' : 'secondary'}
                            disabled={Boolean(busyAction) || !canUpdateStatus}
                            onClick={() => void transitionSelectedItem(status)}
                          >
                            {busyAction === `status:${status}` ? 'Saving…' : formatStatusLabel(status)}
                          </Button>
                        )) : (
                          <span className="forYouMuted">No helper-style execution transition is available from this status.</span>
                        )}
                      </div>
                      <div className="forYouProofRow">
                        <HiddenFileInput
                          ref={proofInputRef}
                          onFiles={(files) => {
                            const file = files[0]
                            if (file) void uploadSelectedProof(file)
                          }}
                        />
                        <Button
                          variant="tertiary"
                          icon={<UploadRoundedIcon />}
                          disabled={Boolean(busyAction) || !canUploadProof}
                          onClick={() => proofInputRef.current?.click()}
                        >
                          {busyAction === 'proof' ? 'Uploading…' : hasProof(selectedItem) ? 'Replace proof' : 'Upload proof'}
                        </Button>
                        {busyAction ? <MiniLoader size="xs" label="Working" inline /> : null}
                      </div>
                    </>
                  ) : (
                    <div className="forYouReadOnlyNotice">
                      <VisibilityRoundedIcon />
                      <div>
                        <strong>{selectedActionPolicy.readOnlyTitle}</strong>
                        <p>{selectedActionPolicy.readOnlyBody}</p>
                      </div>
                    </div>
                  )}
                </section>

                <section className="forYouInspectorCard forYouInspectorCard--wide">
                  <span className="settingsEyebrow">Attachments</span>
                  {attachments.length > 0 ? (
                    <div className="forYouAttachmentList">
                      {attachments.map((attachment) => (
                        <a key={attachment.id} href={attachment.url} target="_blank" rel="noreferrer" className="forYouAttachmentRow">
                          <AttachFileRoundedIcon />
                          <span>
                            <strong>{attachment.name}</strong>
                            <small>{attachment.kind.toLowerCase()} · {attachment.meta || 'Workspace attachment'}</small>
                          </span>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="forYouMuted">No proofs, memos, or comment attachments have been recorded for this item.</p>
                  )}
                </section>

                <section className="forYouInspectorCard">
                  <span className="settingsEyebrow">Discussion</span>
                  {selectedComments.length > 0 ? (
                    <div className="forYouThread">
                      {selectedComments.slice(0, 6).map((comment) => (
                        <article key={comment.id} className="forYouThreadItem">
                          <header>
                            <strong>{comment.actorName}</strong>
                            <small>{formatDate(comment.createdAt)}</small>
                          </header>
                          <p>{comment.deletedAt ? 'Comment deleted' : comment.body}</p>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <p className="forYouMuted">No discussion recorded yet.</p>
                  )}
                </section>

                <section className="forYouInspectorCard">
                  <span className="settingsEyebrow">Recent activity</span>
                  {selectedActivity.length > 0 ? (
                    <div className="forYouTimeline">
                      {selectedActivity.map((entry) => (
                        <div key={entry.id} className="forYouTimelineRow">
                          <span />
                          <p>
                            <strong>{entry.summary}</strong>
                            <small>{entry.actorName} · {formatDate(entry.createdAt)}</small>
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="forYouMuted">No retained activity for this item.</p>
                  )}
                </section>
              </div>
            </>
          ) : (
            <div className="forYouInspectorEmpty">
              <VisibilityRoundedIcon />
              <div>
                <strong>Select a checklist item</strong>
                <p>Choose something from the queue to inspect attachments, dependency state, and completion readiness.</p>
              </div>
            </div>
          )}
        </section>
      </div>

      {activeFormRequest && activeFormDefinition.form ? (
        <div className="forYouFormOverlay" data-testid="for-you-form-renderer">
          <section className="forYouFormDialog">
            <header>
              <div>
                <span className="settingsEyebrow">Assigned form</span>
                <h2>{activeFormDefinition.form.title}</h2>
              </div>
              <IconButton variant="tertiary" icon={<CloseRoundedIcon fontSize="small" />} label="Close" onClick={() => setActiveFormRequest(null)} />
            </header>
            <FormExperienceScreen
              definition={activeFormDefinition.form}
              initialMode="FILL"
              canRespond={activeFormRequest.status === 'PENDING'}
              canReviewResponses={false}
              compact
              onBack={() => setActiveFormRequest(null)}
              onSubmitted={() => setActiveFormRequest(null)}
            />
          </section>
        </div>
      ) : null}
      <AppDialog
        open={notNeededScopeDialogOpen}
        title={t('NLSChecklistNotNeededTitle')}
        onDismiss={() => setNotNeededScopeDialogOpen(false)}
        actions={[
          {
            label: t('NLSChecklistNotNeededConfirm'),
            variant: 'primary',
            autoFocus: true,
            onClick: async () => {
              const scope = notNeededScopeChoice === 'FROM_PERIOD' ? 'FROM_PERIOD' : null
              setNotNeededScopeDialogOpen(false)
              await transitionSelectedItem('NOT_REQUIRED', { notRequiredScope: scope, skipScopePrompt: true })
            },
          },
          {
            label: 'Cancel',
            variant: 'ghost',
            onClick: () => setNotNeededScopeDialogOpen(false),
          },
        ]}
      >
        <SettingsRadioList
          name="foryou-not-needed-scope"
          value={notNeededScopeChoice}
          onChange={(val) => setNotNeededScopeChoice(val as 'THIS_PERIOD' | 'FROM_PERIOD')}
          options={
            selectedItem?.checklistType === 'WEEKLY'
              ? [
                  { value: 'THIS_PERIOD', label: t('NLSChecklistNotNeededJustThisWeek') },
                  { value: 'FROM_PERIOD', label: t('NLSChecklistNotNeededFromThisWeekOn') },
                ]
              : [
                  { value: 'THIS_PERIOD', label: t('NLSChecklistNotNeededJustThisDay') },
                  { value: 'FROM_PERIOD', label: t('NLSChecklistNotNeededFromTodayOn') },
                ]
          }
        />
      </AppDialog>
        </>
      )}
    />
  )
}
